/**
 * The game's **own** OPL2 (AdLib/YM3812) instrument bank — `SXCODE2.OVL`'s
 * `003:` chunk (docs/formats/sound-music.md § "The `003:` AdLib bank").
 *
 * Red Baron's `.SNG` AdLib device tracks are ordinary MIDI event streams
 * whose program-change numbers index *this* table, not General MIDI and not
 * any stock AdLib bank. The gunfire/engine/wind timbres live here as raw
 * OPL2 operator parameters (feedback 7 + half/pulse-sine waveforms is how
 * 1990 FM sound designers made noise); render the same program numbers
 * through a soundfont instead and every effect comes out as a clean musical
 * tone. Decoding this bank is what makes an authentic AdLib render possible.
 *
 * Layout, VERIFIED against the real chunk (2709 bytes decompressed):
 *
 * ```
 * 0x00 uint8    0x89          SCI0 resource-type tag for "patch" (same
 *                             mechanism as .SNG's 0x84 "sound" tag)
 * 0x01 uint8    0x11          Dynamix sub-format byte
 * 0x02 char[17] "AdLib - REDBARON " (fixed-width, space-padded, no NUL of
 *                             its own — offset 0x13 happens to be 0x00)
 * 0x13          body          a stock Sierra SCI 2690-byte AdLib patch bank
 * ```
 *
 * The 2690-byte body is the 96-instrument variant of the format ScummVM's
 * `MidiDriver_AdLib::loadResource` documents (`engines/sci/sound/drivers/
 * adlib.cpp`; GPL, behaviour-only reference, no code copied): 48 instruments
 * of 28 bytes, a 2-byte separator, then 48 more. Both halves are addressed
 * as `28 * index`, with the second half's addresses biased by the separator
 * — hence the `+ 2` below rather than a flat stride.
 *
 * The separator holds `AB CD`, and every one of the 96 instruments' 26
 * range-constrained fields lands inside its documented range, at this base
 * offset and no other — see `test/opl-bank.test.ts`, which re-derives both
 * facts from the real data on every run.
 *
 * Each 28-byte instrument is two 13-byte operator blocks (modulator, then
 * carrier) plus two waveform-select bytes. The per-operator field order is
 * the classic AdLib "BNK" instrument record — `ksl, mult, feedback, attack,
 * sustain, eg-type, decay, release, level, am, vib, ksr, connection` — where
 * `feedback` and `connection` are channel-wide settings that only the
 * modulator's copy is meaningful for. `connection` is stored **inverted**:
 * 0 selects additive (AM) synthesis, non-zero selects FM.
 */

import { parseChunks, requireLeaf } from "./chunk.ts";
import { decodeCompressedChunk } from "./compression.ts";

/** Number of instrument slots in the bank; program-change numbers index this range. */
export const OPL_BANK_INSTRUMENT_COUNT = 96;

/** SCI0 resource-type tag for a patch resource — byte 0 of the decompressed chunk. */
const PATCH_RESOURCE_TAG = 0x89;
const LABEL_OFFSET = 2;
const LABEL_LENGTH = 17;
const BODY_OFFSET = LABEL_OFFSET + LABEL_LENGTH;
const INSTRUMENT_BYTES = 28;
const OPERATOR_BYTES = 13;
/** Instruments below this index are packed flat; the rest sit past the 2-byte separator. */
const SEPARATOR_INSTRUMENT = 48;
const SEPARATOR_BYTES = 2;
/** The two bytes sitting between instrument 47 and instrument 48. */
export const OPL_BANK_SEPARATOR: readonly [number, number] = [0xab, 0xcd];

/** Byte offset, relative to the bank body, of instrument `index`'s 28-byte record. */
function instrumentOffset(index: number): number {
  const bias = index < SEPARATOR_INSTRUMENT ? 0 : SEPARATOR_BYTES;
  return BODY_OFFSET + bias + INSTRUMENT_BYTES * index;
}

/** One OPL2 operator's parameters, already split out of the packed register fields. */
export interface OplOperator {
  /** Key-scale level, 0-3 — how fast output attenuates with rising pitch (register 0x40 bits 7-6). */
  readonly keyScaleLevel: number;
  /** Frequency multiplier selector, 0-15 (register 0x20 bits 3-0); 0 means x0.5. */
  readonly frequencyMultiplier: number;
  /** Attack rate, 0-15 (register 0x60 bits 7-4); 0 is "never", 15 is instant. */
  readonly attackRate: number;
  /** Decay rate, 0-15 (register 0x60 bits 3-0). */
  readonly decayRate: number;
  /** Sustain level, 0-15 (register 0x80 bits 7-4); the attenuation decay falls to. */
  readonly sustainLevel: number;
  /** Release rate, 0-15 (register 0x80 bits 3-0). */
  readonly releaseRate: number;
  /** Total level (base attenuation), 0-63 — 0 is loudest (register 0x40 bits 5-0). */
  readonly totalLevel: number;
  /** Tremolo enable (register 0x20 bit 7). */
  readonly amplitudeModulation: boolean;
  /** Vibrato enable (register 0x20 bit 6). */
  readonly vibrato: boolean;
  /** Envelope type: `true` holds at the sustain level while the key is down, `false` decays away (register 0x20 bit 5). */
  readonly sustaining: boolean;
  /** Key-scale rate: envelope rates scale with pitch (register 0x20 bit 4). */
  readonly keyScaleRate: boolean;
  /** Waveform select, 0-3: full / half / absolute / pulse sine (register 0xE0). */
  readonly waveform: number;
}

/** One bank slot: the two operators of a 2-operator OPL2 voice plus its channel-wide settings. */
export interface OplInstrument {
  /** Slot number — what an AdLib track's program-change byte selects. */
  readonly index: number;
  readonly modulator: OplOperator;
  readonly carrier: OplOperator;
  /** Modulator self-feedback, 0-7 (register 0xC0 bits 3-1); 7 is the maximum and drives the operator into noise. */
  readonly feedback: number;
  /** `true` sums both operators (AM), `false` routes modulator into carrier (FM) — register 0xC0 bit 0. */
  readonly additive: boolean;
}

export interface OplBank {
  /** The bank's own display label, e.g. `"AdLib - REDBARON"`. */
  readonly label: string;
  /** Always `OPL_BANK_INSTRUMENT_COUNT` entries, indexed by program-change number. */
  readonly instruments: readonly OplInstrument[];
}

function toBytes(source: ArrayBufferLike | ArrayBufferView): Uint8Array {
  return ArrayBuffer.isView(source)
    ? new Uint8Array(source.buffer as ArrayBufferLike, source.byteOffset, source.byteLength)
    : new Uint8Array(source);
}

function byteAt(bytes: Uint8Array, offset: number): number {
  const value = bytes[offset];
  if (value === undefined) {
    throw new RangeError(`OPL bank: read past end of chunk at offset ${offset} (length ${bytes.length})`);
  }
  return value;
}

/** Field positions inside a 13-byte operator block (the AdLib BNK instrument record order). */
const KEY_SCALE_LEVEL = 0;
const FREQUENCY_MULTIPLIER = 1;
const FEEDBACK = 2;
const ATTACK_RATE = 3;
const SUSTAIN_LEVEL = 4;
const ENVELOPE_TYPE = 5;
const DECAY_RATE = 6;
const RELEASE_RATE = 7;
const TOTAL_LEVEL = 8;
const AMPLITUDE_MODULATION = 9;
const VIBRATO = 10;
const KEY_SCALE_RATE = 11;
const CONNECTION = 12;

function decodeOperator(bytes: Uint8Array, offset: number, waveform: number): OplOperator {
  const field = (index: number): number => byteAt(bytes, offset + index);
  return {
    keyScaleLevel: field(KEY_SCALE_LEVEL) & 0x03,
    frequencyMultiplier: field(FREQUENCY_MULTIPLIER) & 0x0f,
    attackRate: field(ATTACK_RATE) & 0x0f,
    decayRate: field(DECAY_RATE) & 0x0f,
    sustainLevel: field(SUSTAIN_LEVEL) & 0x0f,
    releaseRate: field(RELEASE_RATE) & 0x0f,
    totalLevel: field(TOTAL_LEVEL) & 0x3f,
    amplitudeModulation: field(AMPLITUDE_MODULATION) !== 0,
    vibrato: field(VIBRATO) !== 0,
    sustaining: field(ENVELOPE_TYPE) !== 0,
    keyScaleRate: field(KEY_SCALE_RATE) !== 0,
    waveform: waveform & 0x03,
  };
}

function decodeInstrument(bytes: Uint8Array, index: number): OplInstrument {
  const offset = instrumentOffset(index);
  return {
    index,
    modulator: decodeOperator(bytes, offset, byteAt(bytes, offset + 2 * OPERATOR_BYTES)),
    carrier: decodeOperator(bytes, offset + OPERATOR_BYTES, byteAt(bytes, offset + 2 * OPERATOR_BYTES + 1)),
    feedback: byteAt(bytes, offset + FEEDBACK) & 0x07,
    additive: byteAt(bytes, offset + CONNECTION) === 0,
  };
}

/**
 * Decodes a decompressed `003:` chunk payload (starting at its `0x89` patch
 * tag) into the 96-slot instrument bank AdLib program-change numbers index.
 */
export function decodeOplBank(source: ArrayBufferLike | ArrayBufferView): OplBank {
  const bytes = toBytes(source);
  const tag = byteAt(bytes, 0);
  if (tag !== PATCH_RESOURCE_TAG) {
    throw new Error(`OPL bank: expected SCI patch tag 0x89 at offset 0, got 0x${tag.toString(16)}`);
  }
  const end = instrumentOffset(OPL_BANK_INSTRUMENT_COUNT - 1) + INSTRUMENT_BYTES;
  if (bytes.length < end) {
    throw new Error(`OPL bank: chunk is ${bytes.length} bytes, need at least ${end} for ${OPL_BANK_INSTRUMENT_COUNT} instruments`);
  }

  const label = String.fromCharCode(...bytes.subarray(LABEL_OFFSET, LABEL_OFFSET + LABEL_LENGTH)).trim();
  const instruments = Array.from({ length: OPL_BANK_INSTRUMENT_COUNT }, (_unused, index) => decodeInstrument(bytes, index));
  return { label, instruments };
}

/** The chunk tag the AdLib bank lives under inside `SXCODE2.OVL`'s `SSM:` container. */
export const OPL_BANK_CHUNK_TAG = "003:";

/**
 * Pulls the AdLib bank out of a whole `SXCODE2.OVL` (the `SSM:` container of
 * per-device drivers and patch banks): finds `003:`, LZW-decompresses it,
 * and decodes it.
 */
export function decodeOplBankFromOverlay(source: ArrayBufferLike | ArrayBufferView): OplBank {
  const nodes = parseChunks(source);
  const container = nodes[0];
  if (!container || container.kind !== "container") {
    throw new Error("OPL bank: expected a top-level container chunk in the sound overlay");
  }
  const compressed = requireLeaf(container.children, OPL_BANK_CHUNK_TAG, `'${container.tag}' sound overlay`);
  return decodeOplBank(decodeCompressedChunk(compressed));
}
